import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

const BUCKETS = [
  {
    key: "noyau_dur",
    label: "🏆 Noyau dur",
    desc: "≥8 présences en 4 sem · 2+/semaine · paye sans histoire",
    color: "border-emerald-500/40 bg-emerald-500/5",
    txt: "text-emerald-300",
  },
  {
    key: "a_renforcer",
    label: "💪 À renforcer",
    desc: "3-7 présences · ~1×/semaine · encore là",
    color: "border-blue-500/40 bg-blue-500/5",
    txt: "text-blue-300",
  },
  {
    key: "en_danger",
    label: "⚠️ En danger",
    desc: "1-2 présences · à rappeler urgent",
    color: "border-amber-500/40 bg-amber-500/5",
    txt: "text-amber-300",
  },
  {
    key: "absents",
    label: "🚫 Absents",
    desc: "0 présence · à recontacter pour clarifier",
    color: "border-rose-500/40 bg-rose-500/5",
    txt: "text-rose-300",
  },
];

export default function AuditMembresActifsModule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [markingName, setMarkingName] = useState("");
  const [markingClass, setMarkingClass] = useState("Parent/enfant Petit Samouraï");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kaizen-v2/audit/summary?weeks=4");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const importTatamios = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/kaizen-v2/audit/import-tatamios-attendance");
      setImportResult(data);
      await fetchData();
    } finally {
      setImporting(false);
    }
  };

  const quickMark = async () => {
    if (!markingName.trim()) return;
    try {
      await api.post("/kaizen-v2/audit/mark-attendance", {
        person_name: markingName.trim(),
        class_name: markingClass,
        date: new Date().toISOString().slice(0, 10),
      });
      setMarkingName("");
      await fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="text-zinc-400 p-6">Chargement…</div>;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="audit-membres-module">
      <div>
        <h1 className="text-3xl font-bold text-amber-400 tracking-tight">
          📋 Audit Membres Actifs — 4 semaines
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          Découvre qui est vraiment là sur le tatami (vs ZenPlanner pas à jour) · {data.period_weeks} semaines glissantes
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Membres ZP actifs</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{data.totals.total_members}</div>
        </div>
        {BUCKETS.map((b) => (
          <div key={b.key} className={`border rounded-lg p-4 ${b.color}`}>
            <div className="text-xs uppercase text-zinc-500">{b.label}</div>
            <div className={`text-2xl font-bold mt-1 ${b.txt}`}>{data.totals[b.key]}</div>
          </div>
        ))}
      </div>

      {/* QUICK ACTIONS */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <h2 className="text-lg font-bold text-amber-300">⚡ Marquer une présence rapide</h2>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={markingName}
            onChange={(e) => setMarkingName(e.target.value)}
            placeholder="Nom du membre (ex: Émile Dénommé)"
            data-testid="quick-mark-name"
            className="flex-1 min-w-[200px] bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100"
          />
          <select
            value={markingClass}
            onChange={(e) => setMarkingClass(e.target.value)}
            data-testid="quick-mark-class"
            className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100"
          >
            <option>Parent/enfant Petit Samouraï</option>
            <option>BJJ Junior</option>
            <option>BJJ Adulte</option>
            <option>Jiu-jitsu Can Ryu Junior PCV</option>
            <option>Jiu-jitsu Can Ryu Junior PCN</option>
            <option>Muay-Thai / Kick Junior</option>
            <option>Muay-Thai / Kick Adulte</option>
            <option>PowerKick Her</option>
            <option>Drop-In</option>
            <option>Cours privé</option>
          </select>
          <button
            onClick={quickMark}
            data-testid="quick-mark-btn"
            className="px-4 py-2 rounded bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400"
          >
            ✓ Marquer présent
          </button>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={importTatamios}
            disabled={importing}
            data-testid="import-tatamios-btn"
            className="px-4 py-2 rounded bg-zinc-800 text-zinc-200 text-sm font-semibold hover:bg-zinc-700"
          >
            {importing ? "Import…" : "📥 Importer présences (export)"}
          </button>
          {importResult && (
            <span className="text-xs text-emerald-300">✓ {importResult.imported} présences importées</span>
          )}
        </div>
      </div>

      {/* BUCKETS */}
      {BUCKETS.map((b) => (
        <section key={b.key} className="space-y-2">
          <h2 className={`text-lg font-bold ${b.txt}`}>
            {b.label} <span className="text-zinc-500 text-sm font-normal">— {b.desc}</span>{" "}
            <span className="text-zinc-400">({data[b.key].length})</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data[b.key].slice(0, 30).map((p, i) => (
              <div
                key={i}
                className={`border rounded p-3 text-sm ${b.color}`}
                data-testid={`${b.key}-person-${i}`}
              >
                <div className="font-semibold text-zinc-100">{p.name}</div>
                <div className="text-xs text-zinc-400 mt-0.5">
                  📧 {p.email || "—"} · {p.presences} présence(s)
                </div>
                {(b.key === "en_danger" || b.key === "absents") && p.phone && (
                  <div className="flex gap-2 mt-2">
                    <a
                      href={`sms:${p.phone}?&body=${encodeURIComponent(
                        `Bonjour ${p.name.split(" ")[0]}, c'est Ralph d'Impact Élite Martial. On t'a pas vu au dojo dernièrement — tout va bien ? Si tu veux discuter, écris-moi.`,
                      )}`}
                      data-testid={`sms-${b.key}-${i}`}
                      className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30"
                    >
                      📱 SMS rappel
                    </a>
                  </div>
                )}
              </div>
            ))}
            {data[b.key].length > 30 && (
              <div className="text-xs text-zinc-500 italic">
                ... et {data[b.key].length - 30} de plus
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
